var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
import React, { memo, useRef, useEffect, useState } from 'react';
import { Map } from 'immutable';
import { Editor, EditorState, getDefaultKeyBinding, CompositeDecorator, ContentState, convertToRaw, convertFromRaw, convertFromHTML, DefaultDraftBlockRenderMap } from 'draft-js';
import { Toolbar } from './Toolbar';
import { Wrapper, EditorWrapper } from './TextEditor.Styles';
import 'draft-js/dist/Draft.css';
import { styled } from '@web/theme';
import { useNewLineEntities, useNonBreakingSpaceEntities, useLinkEntities } from './Decorators/index';
import { usePageEdit } from '../ModulePage';
export var TextEditor = memo(function (_a) {
    var value = _a.value, onChange = _a.onChange, readOnly = _a.readOnly, placeholder = _a.placeholder, color = _a.color, bg = _a.bg, toolbarByFocus = _a.toolbarByFocus;
    var showHelpers = usePageEdit().pageEdit.showHelpers;
    var _b = useLinkEntities(), LinkEntity = _b.LinkEntity, PreventedLinkEntity = _b.PreventedLinkEntity, findLinkEntities = _b.findLinkEntities;
    var _c = useNonBreakingSpaceEntities(readOnly), NonBreakingSpaceEntity = _c.NonBreakingSpaceEntity, findNonBreakingSpaceEntities = _c.findNonBreakingSpaceEntities;
    var _d = useNewLineEntities(readOnly), NewLineEntity = _d.NewLineEntity, findNewLineEntities = _d.findNewLineEntities;
    var _e = useState(function () { return new CompositeDecorator([]); }), decorator = _e[0], setDecorator = _e[1];
    var _f = useState(EditorState.moveFocusToEnd(EditorState.createEmpty(decorator))), editorState = _f[0], setEditorState = _f[1];
    useEffect(function () {
        setDecorator(new CompositeDecorator([
            { strategy: findLinkEntities, component: readOnly ? LinkEntity : PreventedLinkEntity, },
            { strategy: findNonBreakingSpaceEntities, component: NonBreakingSpaceEntity, },
            { strategy: findNewLineEntities, component: NewLineEntity, },
        ]));
    }, [readOnly, showHelpers]);
    useEffect(function () {
        var isJson = function (value) {
            try {
                JSON.parse(value);
            }
            catch (e) {
                return false;
            }
            return true;
        };
        var fromHtml = function (value) {
            var blocksFromHTML = convertFromHTML(value);
            return ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);
        };
        var createContent = function (value) { return value
            ? EditorState.moveFocusToEnd(EditorState.createWithContent(isJson(value)
                ? convertFromRaw(JSON.parse(value))
                : fromHtml(value), decorator))
            : EditorState.moveFocusToEnd(EditorState.createEmpty(decorator)); };
        setEditorState(createContent(value));
    }, [value, decorator]);
    var editor = useRef(null);
    var focus = function () { var _a; return (_a = editor === null || editor === void 0 ? void 0 : editor.current) === null || _a === void 0 ? void 0 : _a.focus(); };
    var _onChange = function (state) {
        var getEditorString = function (s) {
            var content = s.getCurrentContent();
            return content.hasText()
                ? JSON.stringify(convertToRaw(content))
                : '';
        };
        var oldString = getEditorString(editorState);
        var newString = getEditorString(state);
        setEditorState(state);
        if (oldString !== newString)
            onChange(newString);
    };
    var onToolbarButtonClick = function (state) {
        _onChange(state);
        setTimeout(function () { return focus(); }, 0);
    };
    var _keyBindingFn = function (e) {
        if (e.keyCode === 9) {
            return 'editor-tab';
        }
        return getDefaultKeyBinding(e);
    };
    var _handleKeyCommand = function (command, editorState) {
        if (command === 'editor-tab') {
            console.log('tab');
            return 'handled';
        }
        console.log('not TAB');
        return 'not-handled';
    };
    var blockRenderMap = Map({
        '__': {
            element: Paragraph,
            aliasedElements: ['p'],
        }
    });
    var extendedBlockRenderMap = DefaultDraftBlockRenderMap.merge(blockRenderMap);
    function myBlockStyleFn(contentBlock) {
        var type = contentBlock.getType();
        if (type === 'unstyled')
            return 'unstyled';
        if (type.substr(0, 7) === 'header-')
            return 'header';
    }
    return (React.createElement(Wrapper, null,
        React.createElement(Toolbar, { readOnly: readOnly, editorState: editorState, onChange: onToolbarButtonClick, color: color, bg: bg }),
        React.createElement(EditorWrapper, { onClick: focus },
            React.createElement(Editor, { ref: editor, editorState: editorState, onChange: _onChange, blockRenderMap: extendedBlockRenderMap, keyBindingFn: _keyBindingFn, handleKeyCommand: _handleKeyCommand, placeholder: !readOnly ? placeholder : '', spellCheck: true, readOnly: readOnly, blockStyleFn: myBlockStyleFn }))));
});
var Paragraph = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n\tmargin-bottom: .5rem;\n"], ["\n\tmargin-bottom: .5rem;\n"])));
var templateObject_1;
